'use client';

import clsx from 'clsx';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import React from 'react';

import { DirectionsArrowLeft, DirectionsArrowRight } from '../../icons/directions';
import { ButtonPrimary } from '../buttons/ButtonPrimary';
import { Typography } from '../typography/Typography';

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  isFullScreen?: boolean
  defaultSelectedIndex?: number
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
  nextSlideLabel: string
  prevSlideLabel: string
  indicatorSeparatorLabel: string
  closeOverlayLabel: string
  testid?: string;
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  selectedIndex: number
  isSingle: boolean
  carouselLength: number
  nextSlideLabel: string
  prevSlideLabel: string
  indicatorSeparatorLabel: string
  closeOverlayLabel: string
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

export function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

export const Carousel = ({
  isFullScreen = false,
  defaultSelectedIndex = 0,
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  nextSlideLabel,
  prevSlideLabel,
  indicatorSeparatorLabel,
  closeOverlayLabel,
  testid,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & CarouselProps) => {
  // Otherwise when using the arrow you won't get the animation as it jumps to the next
  const initialSelectedIndexRef = React.useRef(defaultSelectedIndex);

  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      startIndex: initialSelectedIndexRef.current,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );

  const [isSingle, setIsSingle] = React.useState(
    !api?.slideNodes().length
    || api.slideNodes().length === 1,
  );

  const [selectedIndex, setSelectedIndex] = React.useState(defaultSelectedIndex);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  const carouselLength = api?.slideNodes().length ?? 1;

  React.useEffect(() => {
    setIsSingle(carouselLength === 1);
    api?.reInit({
      active: carouselLength > 1,
    });
  }, [api, carouselLength]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        selectedIndex,
        isSingle,
        carouselLength,
        isFullScreen,
        nextSlideLabel,
        prevSlideLabel,
        indicatorSeparatorLabel,
        closeOverlayLabel,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={clsx('relative', className)}
        role="region"
        aria-roledescription="carousel"
        data-testid={testid}
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

export const CarouselContent = ({ children }: {
  children: React.ReactNode;
}) => {
  const {
    carouselRef,
    isFullScreen,
  } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={clsx(!isFullScreen && 'overflow-hidden')}
    >
      <div
        className={clsx(
          'relative flex',
          !isFullScreen && 'gap-4',
          isFullScreen && 'gap-14 [@media(max-height:30rem)]:gap-4',
        )}
      >
        {children}
      </div>
    </div>
  );
};

export const CarouselItem = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      role="group"
      aria-roledescription="slide"
      className={clsx(
        'min-w-0 shrink-0 grow-0 basis-full',
        className,
      )}
      {...props}
    />
  );
};

const CarouselPrevious = () => {
  const {
    scrollPrev,
    canScrollPrev,
    isFullScreen,
    prevSlideLabel,
  } = useCarousel();

  return (
    <div
      className={clsx(
        'flex items-center justify-center pointer-events-auto absolute',
        !isFullScreen && 'left-4 top-1/2 -translate-y-1/2',
        isFullScreen && 'left-4 max-md:-bottom-32 max-md:translate-y-1/4 max-md:[@media(max-height:30rem)]:top-1/2 max-md:[@media(max-height:30rem)]:-translate-y-1/2 md:-left-16 md:top-1/2',
      )}
    >
      <ButtonPrimary
        iconOnly
        icon={DirectionsArrowLeft}
      >
        <button
          onClick={scrollPrev}
          disabled={!canScrollPrev}
        >
          <span className="sr-only">
            {prevSlideLabel}
          </span>
        </button>
      </ButtonPrimary>
    </div>
  );
};

const CarouselNext = () => {
  const {
    scrollNext,
    canScrollNext,
    isFullScreen,
    nextSlideLabel,
  } = useCarousel();

  return (
    <div
      className={clsx(
        'flex items-center justify-center pointer-events-auto absolute',
        !isFullScreen && 'right-4 top-1/2 -translate-y-1/2',
        isFullScreen && 'right-4 max-md:-bottom-32 max-md:translate-y-1/4 max-md:[@media(max-height:30rem)]:top-1/2 max-md:[@media(max-height:30rem)]:-translate-y-1/2 md:-right-16 md:top-1/2',
      )}
    >
      <ButtonPrimary
        iconOnly
        icon={DirectionsArrowRight}
        testid={nextSlideLabel}
      >
        <button
          disabled={!canScrollNext}
          onClick={scrollNext}
        >
          <span className="sr-only">
            {nextSlideLabel}
          </span>
        </button>
      </ButtonPrimary>
    </div>
  );
};

export const CarouselNavigationButtons = () => {
  const {
    isSingle,
    isFullScreen,
  } = useCarousel();

  if (isSingle) return null;

  return (
    <div
      className={clsx(
        'pointer-events-none absolute left-0 top-0 w-full',
        !isFullScreen && 'aspect-[4/3]',
        isFullScreen && 'absolute max-md:bottom-0 md:size-full',
      )}
    >
      <CarouselPrevious />
      <CarouselNext />
    </div>
  );
};

export const CarouselIndicator = () => {
  const {
    selectedIndex,
    isSingle,
    isFullScreen,
    carouselLength,
    api,
    indicatorSeparatorLabel,
  } = useCarousel();

  if (isSingle) return null;

  const shouldShowText = carouselLength > 5;

  return (
    <div
      className={clsx(
        'pointer-events-none mt-4 flex w-full justify-center align-center gap-1',
        isFullScreen && 'absolute -bottom-32 [@media(max-height:30rem)]:bottom-2',
        isFullScreen && '[@media(max-height:30rem)]:mt-0',
      )}
    >
      {shouldShowText ? (
        <Typography
          variant="label-regular"
          textColorClassname="text-image-caption"
        >
          {selectedIndex + 1} {indicatorSeparatorLabel} {carouselLength}
        </Typography>
      ) : (
        api?.slideNodes().map((node, index) => (
          <div
            key={node.innerHTML}
            className={clsx('size-2 rounded-full transition-colors duration-700', {
              'bg-image-dot-active': selectedIndex === index,
              'bg-image-dot-inactive': selectedIndex !== index,
            })}
          />
        ))
      )}
    </div>
  );
};
